import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import CapabilityType from "@/utils/constants/CapabilityType.ts";
import CurrentUserUtils from "@/utils/login/CurrentUserUtils.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import CriteriaBuilder from "@/utils/criteria/CriteriaBuilder.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import ExpressionBuilder from "@/utils/criteria/ExpressionBuilder.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import Operator from "@/utils/constants/Operator.ts";
import EntityTypesUtils from "@/utils/common/EntityTypesUtils.ts";
import ExpressionRelationship from "@/utils/constants/ExpressionRelationship.ts";
import Translator from "@/utils/common/Translator.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import BaseDispatcher from "@/utils/presenters/BaseDispatcher.ts";
import CriteriaUtils from "@/utils/criteria/CriteriaUtils.ts";
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import ListPageMatchingRequest = namaTypes.ListPageMatchingRequest;
import EntityReferenceData = namaTypes.EntityReferenceData;
import BaseEntityDTO = namaTypes.BaseEntityDTO;
import DTOCriteria = namaTypes.DTOCriteria;
import GenericValue = namaTypes.GenericValue;

export interface SearchForParams
{
  query: string;
  referencedEntityType: string;
  contextInfo: FieldActionContextInfo | undefined | null;
  ownerEntityType: string;
  fieldId: string;
  extraCriteriaId?: string;
  row?: number;
  criteria?: DTOCriteria;
  extraCriteria?: DTOCriteria;
}

export interface SuggestionParams
{
  entityType: string;
  fieldId: string;
  contextFields: Record<string, GenericValue>
  rowIndex?: number,
  currentText: string | null | undefined,
  placeExtraFilter: any
}

export default class SearchingAndSuggestionUtil
{

  static async searchFor(p: SearchForParams) {
    if (!SecurityUtils.userCan(p.referencedEntityType, CapabilityType.SearchView))
      return [];
    let minSearchQueryLength = CurrentUserUtils.fetchMinSearchQueryLength(p.referencedEntityType);
    if (!ClientCache.getCustomGUISettings() || !ClientCache.getCustomGUISettings()!.searchWithConnectedNamesInRefs)
      p.query = p.query.replace(" ", "%");
    if (p.query.length < minSearchQueryLength)
      return;
    let serviceRequest: ListPageMatchingRequest = {entityType: p.referencedEntityType};
    serviceRequest.orderBy = CommonFieldIds.CODE;
    let criteria = CriteriaBuilder.create().field(CommonFieldIds.COMMITE_BEFORE).equal("true");
    criteria = SearchingAndSuggestionUtil.addQueryCriteria(p.query, criteria, p.referencedEntityType, p.fieldId, p.ownerEntityType);
    serviceRequest.extraCriteria = CriteriaUtils.joinCriteria(criteria.build(), p.extraCriteria);
    serviceRequest.criteria = p.criteria;
    serviceRequest.page = 0;
    serviceRequest.pageSize = 25;
    serviceRequest.extraCriteriaId = p.extraCriteriaId;
    let req = {
      request: serviceRequest,
      info: p.contextInfo ?? {}
    };
    //todo
    // req.inChangeDimsDialog = inChangeDimsDialog;
    let list = await NamaCommon.namaFetch(NamaURLS.LIST_REFERENCES, {
      data: req,
      noLoadingIndicator: true
    }) as EntityReferenceData[];
    if (!list || !list.length)
      return [];
    SearchingAndSuggestionUtil.enhanceListForDisplaying(list, p);
    return list;
  }

  static enhanceListForDisplaying(list: namaTypes.EntityReferenceData[], p: SearchForParams) {
    list.forEach(l => {
      l['optionText'] = SearchingAndSuggestionUtil.createOptionText(l);
      if (!l['optionCaption'])
        l['optionCaption'] = SearchingAndSuggestionUtil.createOptionCaption(l);
      l['optionDirection'] = SearchingAndSuggestionUtil.directionIfNeeded(p.ownerEntityType, p.fieldId);
      l['optionHasColorOrImg'] = l.colorCode || SearchingAndSuggestionUtil.shouldShowImgInHeader(p.referencedEntityType);
    })
  }

  static async suggest(p: SuggestionParams) {
    if (!p.rowIndex)
      p.rowIndex = -1;
    let list = await NamaCommon.namaFetch(NamaURLS.PERFORM_Suggestion, {
      data: {
        parameters: {
          entityType: p.entityType,
          contextFields: p.contextFields,
          barCodeOn: LoginUtils.getBarCodeOn(),
          userId: LoginUtils.currentUserId(),
          objectIndex: -1,
          parentId: p.fieldId
        },
        currentText: p.currentText,
        entityTypeForGenericReference: p.entityType,
        fieldId: p.fieldId,
        placeExtraFilter: p.placeExtraFilter
      }, noLoadingIndicator: true
    });
    if (!list || !list['suggestions'])
      return [];
    let suggestionList = list['suggestions'] as namaTypes.SuggestionProvidedItem[]
    suggestionList.forEach(l => {
      if (l.text && l.text.code) {
        l['optionText'] = l.text.code;
        l['optionDisplayCode'] = l.displayCode ?? l.text.code;
        if (l.text.code !== Translator.getName(l.text.name1, l.text.name2))
          l['optionCaption'] = Translator.getName(l.text.name1, l.text.name2);
      }
    })
    return suggestionList;
  }

  static addQueryCriteria(query: string, criteria: ExpressionBuilder, referencedEntityType: string, fieldId: string,
                          ownerEntityType: string) {
    if (!query)
      return criteria;
    let codeOperator = ObjectChecker.getFirstNotEmptyObj(ClientCache.calcReferenceCodeOperator(ownerEntityType, fieldId), ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.codeSearchOperator, Operator.Contains);
    let name1Operator = ObjectChecker.getFirstNotEmptyObj(ClientCache.calcReferenceName1Operator(ownerEntityType, fieldId), ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.name1SearchOperator, Operator.Contains);
    let name2Operator = ObjectChecker.getFirstNotEmptyObj(ClientCache.calcReferenceName2Operator(ownerEntityType, fieldId), ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.name2SearchOperator, Operator.Contains);
    criteria = criteria.and().openBracket();
    if (EntityTypesUtils.isMaster(referencedEntityType)) {
      criteria = criteria.field(CommonFieldIds.CODE).contains(query).operator(codeOperator).or().field(CommonFieldIds.NAME1).contains(query)
        .operator(name1Operator).or().field(CommonFieldIds.NAME2).contains(query).operator(name2Operator);
      if (EntityTypesUtils.hasAltCode(referencedEntityType))
        criteria = criteria.or().field(CommonFieldIds.ALT_CODE).contains(query).operator(codeOperator);
      criteria = criteria.relationship(ExpressionRelationship.OR);
    } else {
      if (EntityTypesUtils.hasAltCode(referencedEntityType))
        criteria = criteria.field(CommonFieldIds.CODE).contains(query).operator(codeOperator).or().field(CommonFieldIds.ALT_CODE)
          .contains(query).operator(codeOperator);
      else
        criteria = criteria.field(CommonFieldIds.CODE).contains(query).operator(codeOperator);
    }
    let settings = ClientCache.calcExtraReferenceSearchSettings(referencedEntityType);
    if (settings) {
      for (let setting of settings)
        criteria = criteria.or().field(setting.fieldId).contains(query).operator(setting.operator);
    }
    criteria = criteria.closeBracket();
    return criteria;
  }

  static createOptionText(l?: namaTypes.EntityReferenceData) {
    if (ObjectChecker.isEmptyOrNull(l))
      return "";
    return Translator.getName(l.code, l.altCode);
  }

  static createOptionCaption(l: namaTypes.EntityReferenceData) {
    return Translator.getNameForTitle(l);
  }

  static shouldShowImgInHeader(referencedEntityType: string) {
    if (!referencedEntityType)
      return false;
    let entityImageInfo = ClientCache.getEntityImageInfo(referencedEntityType);
    return entityImageInfo && entityImageInfo.useInHeaderFields;
  }

  static directionIfNeeded(ownerEntityType: string, ownerFieldId: string) {
    let fieldStyle = ClientCache.getFieldStyle(ownerEntityType, ownerFieldId);
    if (!fieldStyle || !fieldStyle.suggestionDirection)
      return "";
    return fieldStyle.suggestionDirection === "RTL" ? "rtl" : "ltr";
  }

  static hasDescriptor(ref: EntityReferenceData) {
    if (!ref || !ref.entityType || !ref.code)
      return false;
    if (ref['optionCaption'])
      return ref['optionCaption'] !== SearchingAndSuggestionUtil.createOptionText(ref);
    return SearchingAndSuggestionUtil.createOptionCaption(ref) !== SearchingAndSuggestionUtil.createOptionText(ref);
  }

  static shouldShowImgInSuggest(referencedEntityType: string) {
    if (!referencedEntityType)
      return false;
    let entityImageInfo = ClientCache.getEntityImageInfo(referencedEntityType);
    return entityImageInfo && entityImageInfo.useInSuggestion;
  }

  static async findByBusinessCode(entityType: string, code: string, searchContextInfo?: FieldActionContextInfo) {
    if (!code)
      return undefined;
    let result = await NamaCommon.namaFetch(NamaURLS.FIND_BY_CODE, {
      params: {
        entityType: entityType,
        code: code
      },
      data: searchContextInfo,
      doNotPushErrorWhenFailure: true
    });
    if (!NamaCommon.isSuccess(result))
      return undefined;
    return result as BaseEntityDTO;
  }

  static createCommonSearchContextInfo(entityType: string, fieldId: string, lineNumber?: number) {
    const contextFields = {};
    for (let dim of BaseDispatcher.commonContextFields_Dimensions) {
      contextFields[dim] = LoginUtils.currentUserInfo()![dim];
    }
    let searchContextInfo: FieldActionContextInfo = {};
    searchContextInfo.souceLineLinumber = lineNumber;
    searchContextInfo.sourceFieldId = fieldId;
    searchContextInfo.sourceEntityType = entityType;
    searchContextInfo.contextFields = contextFields;
    return searchContextInfo;
  }
}
